// src/pages/AgendaPosa.js
// Agenda POSA — tooltip stile AgendaPro, salvataggi robusti, badge Senior/Chiusura

/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import itLocale from "@fullcalendar/core/locales/it";

import "./agenda-posa.css";
import { useAuth } from "../context/AuthContext";

/* ============================================================
   ENDPOINTS
   ============================================================ */
const API_POSA  = "/preventivi/agenda_posa/api"; // listP_events.php, updateP_event.php, ...
const API_USERS = "/preventivi/api";             // users_list.php

/* ============================================================
   Helper di base
   ============================================================ */
/* ============================================================
   Helper di base
   ============================================================ */

/** Padding a 2 cifre (01, 02, …) */
const pad2 = (n) => String(n).padStart(2, "0");

/** Escape per chiavi in regex dinamiche */
const escapeRegExp = (s) =>
  s ? String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") : "";

/** Rimuove eventuali millisecondi da una stringa data/ora */
const stripMs = (s) => String(s).split(".")[0];

/**
 * Converte una data SQL-like ("YYYY-MM-DD HH:mm:ss[.SSS]") in ISO compatibile ("YYYY-MM-DDTHH:mm:ss")
 * - Se è già ISO, si limita a rimuovere i millisecondi.
 * - Restituisce stringa vuota se input falsy.
 */
const sqlToIso = (s) => {
  if (!s) return "";
  const t = String(s).trim();
  if (!t) return "";
  // sostituisce la prima sequenza di spazi tra data e ora con 'T'
  const replaced = t.replace(/\s+/, "T");
  return stripMs(replaced);
};

/**
 * Converte un oggetto Date valido in stringa SQL "YYYY-MM-DD HH:mm:ss".
 * Restituisce stringa vuota per Date non valide o input non-Date.
 */
const dateToSql = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return (
    `${d.getFullYear()}-` +
    `${pad2(d.getMonth() + 1)}-` +
    `${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:` +
    `${pad2(d.getMinutes())}:` +
    `${pad2(d.getSeconds())}`
  );
};

/**
 * Restituisce la fascia oraria:
 * - "M" (Mattina)    h < 13
 * - "P" (Pomeriggio) 13 <= h < 18
 * - "S" (Sera)       h >= 18
 * Restituisce stringa vuota se data non valida.
 */
const fasciaFromDate = (dOrStr) => {
  const d = dOrStr instanceof Date ? dOrStr : new Date(sqlToIso(dOrStr));
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const h = d.getHours();
  if (h < 13) return "M";
  if (h < 18) return "P";
  return "S";
};

/**
 * Estrae un tag in forma [key:valore] da una nota.
 * Esempio: note="Lorem [PRIORITA: alta]" -> getNoteTag(note, "priorita") => "alta"
 * Case-insensitive sulla chiave.
 */
const getNoteTag = (note, key) => {
  if (!key) return null;
  const re = new RegExp(`\\[${escapeRegExp(String(key))}:([^\\]]+)\\]`, "i");
  const m = String(note ?? "").match(re);
  return m ? m[1].trim() : null;
};

/**
 * Effettua una GET con fetch, gestendo JSON/errore in modo robusto.
 * @param {string} url
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs=15000]
 * @param {RequestCredentials} [opts.credentials="same-origin"]
 * @param {RequestCache} [opts.cache="no-store"]
 * @param {HeadersInit} [opts.headers]
 */
async function apiGet(
  url,
  { timeoutMs = 15000, credentials = "same-origin", cache = "no-store", headers = {} } = {}
) {
  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(new Error("timeout")), timeoutMs);
  let r, t;
  try {
    r = await fetch(url, {
      method: "GET",
      credentials,
      cache,
      headers,
      signal: controller.signal,
    });
    t = await r.text();
  } finally {
    clearTimeout(to);
  }
  let j;
  try {
    j = JSON.parse(t);
  } catch {
    j = Array.isArray(t) ? t : [];
  }
  if (!r.ok) throw new Error((j && j.message) || `HTTP ${r.status}`);
  return j;
}

/**
 * Effettua una POST JSON (o FormData) con routing opzionale su API_POSA.
 * - Se endpoint è assoluto (http/https), lo usa direttamente.
 * - Se inizia con "/preventivi/", lo usa così com'è (same-origin).
 * - Altrimenti premette `${base}/${endpoint}` dove base di default è `API_POSA` se definita.
 *
 * @param {string} endpoint
 * @param {object|FormData} [payload={}]
 * @param {object} [opts]
 * @param {string} [opts.base=API_POSA] Base URL (se non assoluto e non /preventivi/)
 * @param {number} [opts.timeoutMs=15000]
 * @param {RequestCredentials} [opts.credentials="same-origin"]
 * @param {HeadersInit} [opts.headers]
 */
async function apiPost(
  endpoint,
  payload = {},
  {
    base = typeof API_POSA !== "undefined" ? API_POSA : "",
    timeoutMs = 15000,
    credentials = "same-origin",
    headers,
  } = {}
) {
  const isAbsolute = /^https?:\/\//i.test(endpoint);
  const url = isAbsolute
    ? endpoint
    : endpoint.startsWith("/preventivi/")
      ? endpoint
      : `${base}/${endpoint}`.replace(/([^:]\/)\/+/g, "$1"); // evita // multipli

  const isForm = typeof FormData !== "undefined" && payload instanceof FormData;
  const hdrs = isForm ? {} : { "Content-Type": "application/json" };

  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(new Error("timeout")), timeoutMs);
  let r, t;
  try {
    r = await fetch(url, {
      method: "POST",
      headers: { ...hdrs, ...(headers || {}) },
      credentials,
      cache: "no-store",
      body: isForm ? payload : JSON.stringify(payload),
      signal: controller.signal,
    });
    t = await r.text();
  } finally {
    clearTimeout(to);
  }
  let j;
  try {
    j = JSON.parse(t);
  } catch {
    j = {};
  }
  if (!r.ok || (j.status && j.status !== "OK")) {
    const msg = j.hint || j.message || `HTTP ${r.status}`;
    throw new Error(msg);
   }
 return j;
}

/* ============================================================
   Helpers tooltip / audit
   ============================================================ */

/**
 * Formatta una data/ora (SQL o ISO) in stile italiano: "dd/mm/yyyy, hh:mm".
 * Restituisce stringa vuota se input non valido.
 */
const fmtWhenIt = (sqlOrIso) => {
  const iso = sqlToIso(sqlOrIso);
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = pad2(d.getDate()),
    mm = pad2(d.getMonth() + 1),
    yyyy = d.getFullYear();
  const hh = pad2(d.getHours()),
    mi = pad2(d.getMinutes());
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

/**
 * Ritorna il primo valore "valido" tra le chiavi indicate (non undefined, non null, non stringa vuota).
 */
const pickFirst = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj && obj[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
};

/**
 * Converte in JSON se stringa, altrimenti:
 * - se è già object/array lo restituisce,
 * - se non convertibile -> null.
 */
const safeJson = (x) => {
  if (!x) return null;
  if (Array.isArray(x) || (typeof x === "object" && x)) return x;
  try {
    return JSON.parse(String(x));
  } catch {
    return null;
  }
};

/**
 * Normalizza una lista di eventi audit in forma uniforme.
 * Accetta:
 *  - Array diretto
 *  - Oggetti con `rows`, `data`, o `items`
 *
 * Output ordinato DESC per data (`at`).
 * Campi standardizzati:
 *  - at   : data/ora dell'evento (stringa originale, non formattata)
 *  - byId : id utente / operatore
 *  - byNm : nome utente / operatore
 *  - act  : azione maiuscola (CREATE, UPDATE, ecc.)
 *  - note : testo/nota
 */
const normalizeAudit = (raw) => {
  const arr = Array.isArray(raw) ? raw : (raw?.rows || raw?.data || raw?.items || []);
  const out = [];
  for (const it of arr) {
    const at = pickFirst(it, ["at", "ts", "time", "datetime", "when", "created_at", "dt"]);
    out.push({
      at,
      byId: pickFirst(it, ["by", "user_id", "uid", "by_id", "operator_id", "who_id"]),
      byNm: pickFirst(it, [
        "by_name",
        "user_name",
        "operator_name",
        "who",
        "who_name",
        "user",
        "user_fullname",
        "name",
      ]),
      act: String(pickFirst(it, ["action", "type", "event", "op", "evt"]) || "").toUpperCase(),
      note: pickFirst(it, ["note", "details", "msg", "message", "desc", "description"]) || "",
    });
  }
  out.sort((a, b) => {
    const ta = new Date(sqlToIso(a.at)).getTime() || 0;
    const tb = new Date(sqlToIso(b.at)).getTime() || 0;
    return tb - ta; // discendente (più recente prima)
  });
  return out;
};

/* ============================================================
   (Opzionale) Export nominativi se usi moduli
   ============================================================ */
// export {
//   pad2,
//   sqlToIso,
//   dateToSql,
//   fasciaFromDate,
//   getNoteTag,
//   apiGet,
//   apiPost,
//   fmtWhenIt,
//   pickFirst,
//   safeJson,
//   normalizeAudit,
// };







/* ===== Tooltip helpers AgendaPro (HTML safe + builder) =================== */

/** Escape HTML per evitare injection */
const escapeHtml = (s = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));

/** URL sicuri: consente http/https o path assoluti del sito */
const safeUrl = (u) => {
  const s = String(u || "").trim();
  if (!s) return "";
  return /^(https?:\/\/|\/)/i.test(s) ? s : "";
};

/** Numero → € string (fallback semplice) */
const euro = (v) => {
  const n = Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return "€ 0";
  return "€ " + n.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

/** Orario HH:mm da SQL/ISO/Date */
const hhmm = (sqlOrIsoOrDate) => {
  const d = sqlOrIsoOrDate instanceof Date ? sqlOrIsoOrDate : new Date(sqlToIso(sqlOrIsoOrDate));
  if (Number.isNaN(d.getTime())) return "--:--";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Crea l’HTML della card tooltip stile AgendaPro, usando i campi disponibili */



function buildAgendaProTipHTML(event, opts = {}) {
  const { allowPhone = true } = opts;

  const ex = event?.extendedProps ?? {};

  // ---- base
  const titolo = event?.title || ex.title || ex.cliente_nome || ex.nome_cliente || "Appuntamento";
  const start  = ex.start_dt || event?.start || ex.start || null;
  const end    = ex.end_dt   || event?.end   || ex.end   || null;
  const oraRange = `${hhmm(start)}${end ? "–" + hhmm(end) : ""}`;
const telRaw = pickFirst(ex, ["cliente_telefono","cliente_tel","telefono","phone","cellulare","mobile","tel"]) || "";
const tel    = (allowPhone || !telRaw) ? telRaw : "••• ••• •••";

  const indir  = pickFirst(ex, ["cliente_indirizzo","indirizzo","address","riferimento"]) || "";
  const comune = pickFirst(ex, ["comune","localita"]) || "";
  const cap    = ex.cap || "";

  // ---- POSA correnti (se ci sono)
// ---- POSA correnti (se ci sono)
const rilPosNm = (pickFirst(ex, ["rilevatore_name"]) || ex?.rilevatore?.nome || "").toString();
const senPosNm = (pickFirst(ex, ["responsabile_name","senior_name"]) || ex?.senior?.nome || "").toString();

// ---- Nomi storici AgendaPro (AP) importati in POSA

 const rilApNm = (
   pickFirst(ex, [
     "ap_rilevatore_name",        // nome fornito dalla view
     "src_rilevatore_name",       // fallback storici
     "rilevatore_ap_name",
     "ril_esecutore_name",
     "rilevatore_name_ap",        // altri alias comuni
     "rilevatore_nome_ap",
     "ril_name_ap"
   ]) || ex?.ap_rilevatore?.nome || ""
 ).toString();

 const senApNm = (
   pickFirst(ex, [
     "ap_senior_name",
     "src_senior_name",
     "senior_ap_name",
     "preventivo_senior_name",
     "responsabile_ap_name",
     "senior_nome_ap"
   ]) || ex?.ap_senior?.nome || ""
 ).toString();

// === MOSTRA SEMPRE i nomi AP nel tooltip (fallback a POSA solo se AP mancano)
const rilNm   = rilApNm || rilPosNm;
const rilHint = ""; // niente etichetta (AP)
const senNm   = senApNm || senPosNm;
const senHint = ""; // niente etichetta (AP)


  const chiNm  = pickFirst(ex, ["chiusura_name","closer_name"]) || "";

  const stato  = pickFirst(ex, ["ui_state_label","status","stato_posa","stato"]) || "";
  const esito  = pickFirst(ex, ["senior_esito","posa_esito","esito_posa","esito"]) || "";

  const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
  const totale  = toNum(pickFirst(ex, ["pv_total","totale","importo_totale","total"]));
  const budgetR = toNum(pickFirst(ex, ["pv_budget_rilevatore_eur","budget_rilevatore","rilevatore_budget","budget_ril"]));
  const budgetS = toNum(pickFirst(ex, ["pv_budget_senior_eur","budget_senior","senior_budget"]));

  // link mappa
  const lat = ex.addr_lat, lng = ex.addr_lng;
  const mapHref = (lat && lng)
    ? `https://maps.google.com/?q=${encodeURIComponent(`${lat},${lng}`)}`
    : ([indir, comune, cap].some(Boolean)
        ? `https://maps.google.com/?q=${encodeURIComponent([indir, comune, cap].filter(Boolean).join(", "))}`
        : "");
  const pdfUrl   = safeUrl(ex.prev_pdf_path || ex.pdf_path);
  const audioUrl = safeUrl(ex.prev_audio_path || ex.audio_path || ex.audio_url);
  const mapUrl   = safeUrl(mapHref);

  const links = [
    pdfUrl   ? `<a href="${pdfUrl}" target="_blank" rel="noopener noreferrer">PDF</a>`   : "",
    audioUrl ? `<a href="${audioUrl}" target="_blank" rel="noopener noreferrer">Audio</a>` : "",
    mapUrl   ? `<a href="${mapUrl}" target="_blank" rel="noopener noreferrer">Mappa</a>`   : ""
  ].filter(Boolean).join(" · ");

  // audit (se disponibile)
  let audit = ex._audit;
  if (!audit) {
    const raw = safeJson(pickFirst(ex, ["audit","logs","history","audit_json","_audit","recent_logs"]));
    if (raw) audit = normalizeAudit(raw);
  }
  const rowsAudit = Array.isArray(audit) && audit.length
    ? audit.slice(0, 6).map(it => {
        const who = it.byNm || (it.byId ? `#${it.byId}` : "");
        return `<div class="ap-tip__audit-item">${escapeHtml(fmtWhenIt(it.at))} · ${escapeHtml(it.act || "UPDATE")}${who ? " · " + escapeHtml(who) : ""}</div>`;
      }).join("")
    : "";

  // ---- render
  return `
<div class="ap-tip">
  <div class="ap-tip__hdr">
    <span class="ap-tip__time">${escapeHtml(oraRange)}</span>
    <span class="ap-tip__title">${escapeHtml(titolo)}</span>
  </div>

  <div class="ap-tip__row">📞 <span>${escapeHtml(tel || "—")}</span></div>
  <div class="ap-tip__row">📍 <span>${escapeHtml(indir || "—")}</span></div>
  <div class="ap-tip__row">🏙️ <span>${escapeHtml([comune, cap].filter(Boolean).join(" · ") || "—")}</span></div>

  <div class="ap-tip__row">🧑‍🔧 <span>Rilevatore: <b>${escapeHtml(rilNm || "—")}</b>${rilHint}</span></div>
  <div class="ap-tip__row">👷 <span>Senior: <b>${escapeHtml(senNm || "—")}</b>${senHint}</span></div>
  <div class="ap-tip__row">✅ <span>Chiusura: <b>${escapeHtml(chiNm || "—")}</b></span></div>

  <hr />
  <div class="ap-tip__row">📊 <span>Totale: <b>${euro(totale)}</b></span></div>
  <div class="ap-tip__row">🎯 <span>Budget rilevatore: <b>${euro(budgetR)}</b></span></div>
  <div class="ap-tip__row">🎯 <span>Budget senior: <b>${euro(budgetS)}</b></span></div>

  ${links ? `<div class="ap-tip__row">🔗 <span>Link: ${links}</span></div>` : ""}

  <hr />
  <div class="ap-tip__row">🏷️ <span>Stato: <b>${escapeHtml(stato || "—")}</b></span></div>
  <div class="ap-tip__row">🧩 <span>Esito: <b>${escapeHtml(esito || "—")}</b></span></div>

  ${rowsAudit ? `<hr /><div class="ap-tip__audit"><div class="ap-tip__audit-title">📝 Modifiche recenti</div>${rowsAudit}</div>` : ""}
</div>`;
}








/** Installa tooltip HTML stile AgendaPro su un elemento evento FC */
function installAgendaProTooltip(rootEl, event, opts = {}) {

  let tipEl = null;

  const create = () => {
    if (tipEl) return;
    tipEl = document.createElement("div");
    tipEl.className = "ap-tip-host";
tipEl.innerHTML = buildAgendaProTipHTML(event, opts);
    document.body.appendChild(tipEl);
    // forza visibilità (classe .on per la transizione)
    requestAnimationFrame(() => { try { tipEl.classList.add("on"); } catch {} });
  };

  const destroy = () => {
    if (!tipEl) return;
    try { tipEl.remove(); } catch {}
    tipEl = null;
  };

  const position = (e) => {
    if (!tipEl) return;
    const pad = 10;
    let x = (e?.clientX ?? 0) + pad;
    let y = (e?.clientY ?? 0) + pad;
    const r = tipEl.getBoundingClientRect();
    const W = window.innerWidth, H = window.innerHeight;
    if (x + r.width  > W - 8) x = Math.max(8, W - r.width  - 8);
    if (y + r.height > H - 8) y = Math.max(8, H - r.height - 8);
    tipEl.style.left = x + "px";
    tipEl.style.top  = y + "px";
  };

  const onEnter = (e) => { create(); position(e); };
  const onMove  = (e) => { position(e); };
  const onLeave = () => { destroy(); };

  rootEl.addEventListener("mouseenter", onEnter);
  rootEl.addEventListener("mousemove",  onMove);
  rootEl.addEventListener("mouseleave", onLeave);

  // funzione di cleanup da chiamare in eventWillUnmount
  return () => {
    destroy();
    rootEl.removeEventListener("mouseenter", onEnter);
    rootEl.removeEventListener("mousemove",  onMove);
    rootEl.removeEventListener("mouseleave", onLeave);
  };
}
















/* ============================================================
   Permessi POSA
   ============================================================ */
function roleKeyFromNorm(roleNorm) {
  const r = String(roleNorm || "").toLowerCase();
  if (/(^|[-_])admin($|[-_])/.test(r)) return "admin";
  if (/(^|[-_])(centralino|call.?center|ufficio|back.?office)($|[-_])/.test(r)) return "centralino";
  if (/(^|[-_])(senior_posa|posa_senior|seniorposa|posa)($|[-_])/.test(r)) return "senior_posa";
  if (/(^|[-_])(senior|tecnico_senior)($|[-_])/.test(r)) return "senior";
  if (/(^|[-_])(rilevatore|ril|junior|tecnico|field)($|[-_])/.test(r)) return "rilevatore";
  if (/(^|[-_])(responsabile|resp)($|[-_])/.test(r)) return "responsabile";
  return "user";
}
function deriveRoleCapsPosa(role) {
  const r = String(role || "user").toLowerCase();
  const base = {
    canMoveEvent: false, canConfirm: false, canAssignRilevatore: false, canAssignSenior: false,
    canCancel: false, canReset: false, canDelete: false, canSetSeniorEsito: false,
    canUsePreventivatore: false, canUploadFiles: false, canEditBudgets: false,
    phoneDelay: false, readOnly: false, restrictAssignedOnly: false,
  };
  switch (true) {
    case ["admin","amministratore","root"].includes(r):
      return { ...base, canMoveEvent: true, canConfirm: true, canAssignRilevatore: true, canAssignSenior: true,
        canCancel: true, canReset: true, canDelete: true, canSetSeniorEsito: true, canUsePreventivatore: true,
        canUploadFiles: true, canEditBudgets: true };
    case ["centralino","callcenter","call center","ufficio","backoffice","back office"].includes(r):
      return { ...base, canMoveEvent: true, canConfirm: true, canAssignRilevatore: true, canCancel: true, canReset: true, canUploadFiles: true };
    case ["rilevatore","rilievo","ril","junior","tecnico","field"].includes(r):
      return { ...base, canMoveEvent: true, phoneDelay: true, canUploadFiles: true };
    case ["senior_posa","seniorposa","posa","posa_senior","senior","tecnico_senior"].includes(r):
      return { ...base, canMoveEvent: true, canAssignRilevatore: true, canAssignSenior: true, canSetSeniorEsito: true, canUploadFiles: true, canEditBudgets: true, phoneDelay: true };
    case ["responsabile_zona","responsabile-di-zona","responsabile","resp"].includes(r):
      return { ...base, readOnly: true, restrictAssignedOnly: true };
    default:
      return base;
  }
}
function PermessiBarPosa({ role, zonaId, caps }) {
  if (!caps) return null;
  const ON = (v) => !!v;
  const b = (k, lbl) => (
    <span key={k} style={{
      display:"inline-block", padding:"3px 8px", borderRadius:999,
      border:"1px solid #e5e7eb", background: ON(caps[k]) ? "#eaffea" : "#f8fafc",
      marginRight:6, fontSize:12, fontWeight:700
    }}>{lbl}: {ON(caps[k]) ? "ON" : "off"}</span>
  );
  return (
    <div style={{margin:"6px 0 10px", padding:"6px 8px", border:"1px dashed #e5e7eb", borderRadius:8}}>
      <strong>Permessi POSA</strong> · ruolo: <b>{role}</b> · zona: <b>{zonaId}</b>
      <div style={{marginTop:6, display:"flex", flexWrap:"wrap", gap:6}}>
        {b("canMoveEvent","move")}
        {b("canAssignRilevatore","assign squadra")}
        {b("canAssignSenior","assign resp")}
        {b("canSetSeniorEsito","esito posa")}
        {b("canCancel","cancel")}
        {b("canReset","reset")}
        {b("canDelete","delete")}
        {b("canUploadFiles","upload")}
        {b("canEditBudgets","budget")}
        {b("readOnly","readOnly")}
        {b("restrictAssignedOnly","solo assegnati")}
        {b("phoneDelay","tel -20′")}
      </div>
    </div>
  );
}

/* ============================================================
   Colori / stile
   ============================================================ */
const UI_COLORS = {
  grey:"#9aa1a6", greenSoft:"#a7f3d0", green:"#10b981", deepGreen:"#065f46",
  teal:"#14b8a6", orange:"#f59e0b", red:"#ef4444", pink:"#fde2e8",
  brownLight:"#d7b59a", borderGreen:"#047857", borderLime:"#65a30d",
  borderRed:"#b91c1c", borderBrown:"#8b5e34", violet:"#8b5cf6", borderViolet:"#6d28d9"
};
const normalizePosaEsito = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "";
  if (["1","ok","si","sì","chiuso","closed","success","successo","true"].includes(s)) return "chiuso";
  if (["0","ko","no","non_chiuso","noclose","fail","false"].includes(s)) return "non_chiuso";
  return s;
};
function mapUiStateToStyle(code = "S0") {
  const c = String(code || "S0").toUpperCase();
  switch (c) {
    case "S6": // CHIUSO
      return { backgroundColor: UI_COLORS.deepGreen, borderColor: UI_COLORS.borderLime, classNames: ["ui-s6","ev-posa-chiuso"] };
    case "S8": // IN CORSO → ARANCIONE
      return { backgroundColor: UI_COLORS.orange,   borderColor: UI_COLORS.borderRed,  classNames: ["ui-s8","ev-posa-corso"] };
    case "S5": // NON CHIUSO → ROSSO
      return { backgroundColor: UI_COLORS.red,      borderColor: UI_COLORS.red,        classNames: ["ui-s5","ev-posa-ko"] };
    case "S4": // soft ko (se usi)
      return { backgroundColor: UI_COLORS.pink,     borderColor: UI_COLORS.pink,       classNames: ["ui-s4","ev-posa-softko"] };
    case "S3":
    case "S3S":
      return { backgroundColor: UI_COLORS.orange,   borderColor: UI_COLORS.borderRed,  classNames: ["ui-s3","ev-posa-attesa"] };
    case "S2": // assegnato
      return { backgroundColor: UI_COLORS.teal,     borderColor: UI_COLORS.teal,       classNames: ["ui-s2","ev-posa-assign"] };
    case "S7": // ANNULLATO → ROSA
      return { backgroundColor: UI_COLORS.pink,     borderColor: UI_COLORS.borderBrown,classNames: ["ui-s7","ev-posa-cancel"] };
    case "S1":
      return { backgroundColor: UI_COLORS.greenSoft,borderColor: UI_COLORS.greenSoft,  classNames: ["ui-s1"] };
    case "S0":
    default:
      return { backgroundColor: UI_COLORS.grey,     borderColor: UI_COLORS.grey,       classNames: ["ui-s0"] };
  }
}

function styleFromDBStrict(ex={}) {
  if (ex.ui_bg) {
    const tags = String(ex.ui_tags || "").split(/\s+/).filter(Boolean);
    const code = String(ex.ui_state_code || ex.ui_state || ex.ui || "").toUpperCase();
    return { backgroundColor: ex.ui_bg, borderColor: ex.ui_border || ex.ui_bg,
      classNames: [...tags, code ? `ui-${code.toLowerCase()}` : ""].filter(Boolean) };
  }
  const code = String(ex.ui_state_code || ex.ui_state || ex.ui || "").toUpperCase();
  return mapUiStateToStyle(code || "S0");
}



function styleFromDBSafe(ex = {}) {
  const codeRaw  = ex.ui_state_code || ex.ui_state || "";
  const code     = String(codeRaw).toUpperCase().trim();  // S0, S6, S7, S8...
  const esitoRaw = ex.posa_esito ?? ex.esito_posa ?? ex.senior_esito ?? ex.esito_senior ?? ex.esito ?? "";
  const esito    = String(esitoRaw).toLowerCase().trim(); // "", "chiuso", "non_chiuso"

  // 1) PRIORITÀ ASSOLUTA al codice UI se presente
  if (code) {
    return mapUiStateToStyle(code); // S8 = arancione, S7 = rosa, S6 = verde, ecc.
  }

  // 2) Fallback su ESITO (se il DB non ha ancora ui_state_code)
  if (esito === "chiuso")     return mapUiStateToStyle("S6"); // verde
  if (esito === "non_chiuso") return mapUiStateToStyle("S5"); // rosso

  // 3) Default neutro
  return mapUiStateToStyle("S0"); // grigio
}



function applyUiStyleFromEx(eventApi, ex = {}) {
  try {
    const s = styleFromDBSafe(ex);
    eventApi.setProp("backgroundColor", s.backgroundColor);
    eventApi.setProp("borderColor", s.borderColor);
    if (Array.isArray(s.classNames) && s.classNames.length) {
      eventApi.setProp("classNames", s.classNames);
    }
  } catch {}
}





/* ============================================================
   Adattatore record API → FC
   ============================================================ */


function adaptPosaEvent(exIn = {}) {
  const ex = { ...exIn };

  // normalizza esito + codice UI
  ex.esito_posa = ex.esito_posa ?? ex.posa_esito ?? ex.esito ?? "";
  ex.ui_state_code = String(ex.ui_state_code || ex.ui_state || "").toUpperCase().trim() || "S0";

  // pick start/end con priorità ai valori "effettivi"
// pick start/end con priorità a planned_start (se presente)
const startIso = sqlToIso(
  pickFirst(ex, ["planned_start", "start", "start_effective", "start_dt", "start_at"])
);

  let endIso = sqlToIso(
    pickFirst(ex, ["end", "end_effective", "planned_end", "end_dt", "end_at"])
  );

  // ➜ se manca la start scarta l’evento (evita crash FC)
  if (!startIso) return null;

  // se manca end, ricalcolalo da start + duration_min
  if (!endIso) {
    const d = new Date(startIso);
    const mins = Number(ex.duration_min || 60);
    if (Number.isFinite(mins)) d.setMinutes(d.getMinutes() + mins);
    endIso = d.toISOString().slice(0, 19); // "YYYY-MM-DDTHH:mm:ss"
  }

  // stile da DB (esito/ui)
  const style = styleFromDBSafe(ex);

  // id forte (stringa non vuota)
  const id =
    (ex.agenda_id ?? ex.id ?? ex.event_id ?? "").toString().trim() ||
    `ev_${Math.random().toString(36).slice(2)}`;

  return {
    id, // FC usa id→publicId internamente
    title: ex.title || ex.cliente_nome || ex.nome_cliente || "Appuntamento",
    start: startIso,
    end:   endIso,
    backgroundColor: style.backgroundColor,
    borderColor:     style.borderColor,
    classNames:      Array.isArray(style.classNames) ? style.classNames : [],
    extendedProps: {
      ...ex,
      agenda_id: ex.agenda_id ?? ex.id ?? null,
      start_dt: startIso.replace("T", " "),
      end_dt:   endIso.replace("T", " "),
      fascia:   ex.fascia || fasciaFromDate(startIso),
    },
  };
}





/* ============================================================
   Render cella evento — chip S/C
   ============================================================ */
function EventContentPosa(arg) {

    if (!arg || !arg.event) return null; // ← guard
  const e  = arg.event;
  const ex = e.extendedProps || {};
  const nome = e.title || ex.cliente_nome || "Cliente";
  const citta = getNoteTag(ex.note, "CITTA") || ex.comune || "";
  const fascia = ex.fascia || "";
  const comuneCap = [citta, ex.cap].filter(Boolean).join(" · ");

  const seniorNm  = ex.responsabile_name || ex.senior_name || "";
  const chiusuraNm= ex.chiusura_name || "";
  const short = (s) => String(s||"").trim().split(/\s+/).slice(0,2).join(" ");

  const chipCss = {
    fontSize:11, fontWeight:700, padding:"2px 6px", borderRadius:999,
    background:"rgba(255,255,255,.22)", border:"1px solid rgba(255,255,255,.35)", lineHeight:1.2
  };

  return (
    <div className="pos-ev">
      <div className="pos-ev__r1">
        <span className="pos-ev__time">{arg.timeText}</span>
        <span className="pos-ev__fascia">{fascia}</span>
      </div>
      <div className="pos-ev__name">{nome}</div>
      {comuneCap && <div className="pos-ev__loc">{comuneCap}</div>}

      <div className="ev-badges" style={{display:"flex", gap:6, flexWrap:"wrap", marginTop:2}}>
        {seniorNm   ? <span style={chipCss}>S: {short(seniorNm)}</span>   : null}
        {chiusuraNm ? <span style={chipCss}>C: {short(chiusuraNm)}</span> : null}
      </div>
    </div>
  );
}






/* ============================================================
   Modale POSA
   ============================================================ */

/* ============================================================
   Modale POSA (versione compatta con Assegnazioni POSA + PDF/NOTE/AUDIO)
   ============================================================ */

function ActionModalPosa(props) {
  const {
    open,
    onClose,
    event,                // FullCalendar EventApi
    operatorId = 0,
    caps: C,
    onDidChange = () => {},
  } = props;

  // ----- snapshot -----
  let ex = {};
  try { ex = (event && event.extendedProps) || {}; } catch { ex = {}; }

  const agendaId = React.useMemo(() => {
    try {
      const exNow = (event && event.extendedProps) || {};
      return Number(exNow.agenda_id ?? event?.id ?? 0);
    } catch { return 0; }
  }, [event]);

  const zonaId = Number(ex.zona_id || 0);

  // Ref stabile alla EventApi passata dal calendario
  const evRef = useRef(event);
  useEffect(() => { evRef.current = event; }, [event]);

  // Flag per bloccare click multipli finché c'è un salvataggio in corso
  const busyRef = useRef(false);

  // ===== Upload: AbortController per interrompere l'upload se chiudi la modale =====
  const uploadAbortRef = useRef(null);

  // ----- upload -----


  async function doUpload(kind, file) {
  // stoppa eventuale upload precedente
  if (uploadAbortRef.current) { try { uploadAbortRef.current.abort(); } catch {} }
  uploadAbortRef.current = new AbortController();

  const fd = new FormData();
  fd.append("agenda_id", String(agendaId));
  fd.append("kind", String(kind));   // "pdf" | "audio" | "foto"
  fd.append("file", file);
  fd.append("updated_by_operator_id", String(operatorId || 0));

  // helper comune per validare la risposta
  const ensureOk = (j, r) => {
    if (!r || !r.ok || !j || j.status !== "OK" || !j.url) {
      const msg = (j && (j.message || j.hint)) || (r && `HTTP ${r.status}`) || "Upload failed";
      throw new Error(msg);
    }
    return j.url;
  };

  // --- TENTATIVO 1: fetch standard (con no-store) ---
  try {
    const r = await fetch(`${API_POSA}/upload_file.php`, {
      method: "POST",
      body: fd,
      credentials: "same-origin",
      headers: { "Accept": "application/json", "Pragma": "no-cache", "Cache-Control": "no-store" },
      cache: "no-store",
      redirect: "follow",
      signal: uploadAbortRef.current.signal,
    });

    // Firefox, se cade a livello di rete, lancia TypeError: "NetworkError..."
    const j = await r.json().catch(() => ({}));
    return ensureOk(j, r);

  } catch (err) {
    // Se l’utente ha chiuso la modale nel frattempo
    if (err?.name === "AbortError") {
      throw new Error("Upload cancellato");
    }

    // --- TENTATIVO 2: fallback XHR (robusto su Firefox + proxy) ---
    // NB: mantiene i cookie (withCredentials=true) e non setta Content-Type (lo fa il browser)
    const url = await new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_POSA}/upload_file.php`, true);
        xhr.withCredentials = true; // same-origin cookie/session
        xhr.responseType = "json";
        xhr.setRequestHeader("Accept", "application/json");
        xhr.setRequestHeader("Pragma", "no-cache");
        xhr.setRequestHeader("Cache-Control", "no-store");

        // integra l’AbortController
        const onAbort = () => { try { xhr.abort(); } catch {} };
        const abortHandler = () => onAbort();
        uploadAbortRef.current?.signal?.addEventListener?.("abort", abortHandler, { once: true });

        xhr.onload = () => {
          // risposta JSON o, se il server non mette application/json, prova a parse manuale
          let j = xhr.response;
          if (!j && typeof xhr.responseText === "string") {
            try { j = JSON.parse(xhr.responseText); } catch { j = null; }
          }
          try {
            const finalUrl = ensureOk(j, { ok: (xhr.status>=200 && xhr.status<300), status: xhr.status });
            resolve(finalUrl);
          } catch (e) {
            reject(e);
          }
        };
        xhr.onerror = () => reject(new Error("Network/XHR error"));
        xhr.ontimeout = () => reject(new Error("Timeout XHR"));
        xhr.send(fd);
      } catch (e) {
        reject(e);
      }
    });

    return url;
  }
}
// ----- stato form -----
const [saving, setSaving]       = useState(false);
const [saved, setSaved]         = useState(false);
const [forbidden, setForbidden] = useState(false);

const [form, setForm] = useState(() => ({
  // AP (sola lettura)
  ril_ap_name:   String(ex.ap_rilevatore_name || ex.src_rilevatore_name || ex.rilevatore_ap_name || ""),
  sen_ap_name:   String(ex.ap_senior_name     || ex.src_senior_name     || ex.senior_ap_name     || ""),
  rilevatore_user_id: Number(ex.rilevatore_id || ex.ril_user_id || 0),

  // POSA (assegnazioni correnti)
  rilevatore_id:     Number(ex.rilevatore_id || ex.ril_user_id || 0),
  owner_user_id:     Number(ex.owner_user_id || ex.senior_user_id || ex.senior_id || 0),
  closed_by_user_id: Number(ex.closed_by_user_id || ex.chiusura_user_id || 0),

  // stato/esito
  stato_posa:  String(ex.stato_posa || ex.status || ""),
  esito_posa:  String(ex.posa_esito || ex.esito  || ""),

  // contratto POSA
  contratto_eur: Number(ex.senior_importo_eur || ex.contratto_eur || ex.contratto || 0),

  // budget (se presente)
  budget_cliente_eur: Number(ex.budget_cliente_eur ?? ex.pv_budget ?? 0),

  // file/url + note
  pdf_path:   String(ex.pdf_path   || ""),
  audio_url:  String(ex.audio_path || ""),   // <-- prefill audio dal DB
  foto_path:  String(ex.foto_path  || ""),
  note:       String(ex.note || ex.notes || ""),
}));
const setField = useCallback((k, v) => setForm(s => ({ ...s, [k]: v })), []);




  // Chiudi modale con tasto ESC (hook sempre chiamato, la condizione è dentro l'effetto)

// Chiudi modale con tasto ESC
useEffect(() => {
  if (!open) return;
  const onKeyDown = (e) => {
    if (e.key === "Escape" || e.code === "Escape") {
      e.preventDefault(); e.stopPropagation();
      try { onClose?.(); } catch {}
    }
  };
  window.addEventListener("keydown", onKeyDown, { capture: true });
  return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
}, [open, onClose]);

// ⬇️ INCOLLA QUI questo nuovo effetto (e rimuovi quello vecchio con if (!open) abort)
const prevOpenRef = useRef(open);
useEffect(() => {
  const wasOpen = prevOpenRef.current;
  if (wasOpen && !open) {
    try { uploadAbortRef.current?.abort(); } catch {}
  }
  prevOpenRef.current = open;
}, [open]);

  // Se la modale si chiude, interrompi eventuale upload in corso
  useEffect(() => {
    if (!open) {
      try { uploadAbortRef.current?.abort(); } catch {}
    }
  }, [open]);

  // riallineo form quando si apre (hook sempre chiamato)
  useEffect(() => {
    if (!open) return;
    let nx = {};
    try { nx = (event && event.extendedProps) || {}; } catch { nx = {}; }
    setForm({
      ril_ap_name:   String(nx.ap_rilevatore_name || nx.src_rilevatore_name || nx.rilevatore_ap_name || ""),
      sen_ap_name:   String(nx.ap_senior_name     || nx.src_senior_name     || nx.senior_ap_name     || ""),
      rilevatore_user_id: Number(nx.rilevatore_id || nx.ril_user_id || 0),
      rilevatore_id:     Number(nx.rilevatore_id || nx.ril_user_id || 0),
      owner_user_id:     Number(nx.owner_user_id || nx.senior_user_id || nx.senior_id || 0),
      closed_by_user_id: Number(nx.closed_by_user_id || nx.chiusura_user_id || 0),

      stato_posa:    String(nx.stato_posa || nx.status || ""),
      esito_posa:    String(nx.posa_esito || nx.esito  || ""),

      contratto_eur: Number(nx.senior_importo_eur || nx.contratto_eur || 0),
      budget_cliente_eur: Number(nx.budget_cliente_eur ?? nx.pv_budget ?? 0),
      pdf_path:      String(nx.pdf_path || ""),
      audio_url:     String(nx.audio_path || ""),
      note:          String(nx.note || nx.notes || ""),
    });
  }, [open, event]);

  // ----- carico utenti di zona per assegnazioni POSA -----
  const [utenti, setUtenti] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!open) return;
    const z = zonaId || 0;
    (async () => {
      try {
        const r = await fetch(`/preventivi/api/users_list.php?zona_id=${z}`, { credentials:"same-origin" });
        const j = await r.json().catch(()=> ({}));
        const arr =
          Array.isArray(j?.rows)  ? j.rows :
          Array.isArray(j?.users) ? j.users :
          Array.isArray(j)        ? j      : [];
        if (alive) setUtenti(arr);
      } catch { if (alive) setUtenti([]); }
    })();
    return () => { alive = false; };
  }, [open, zonaId]);

  const idOf   = useCallback(u => Number(u.id ?? u.user_id ?? u.ID ?? 0), []);
  const nameOf = useCallback(u => (u.nome ?? u.name ?? u.label ?? u.fullname ?? u.username ?? "").toString().trim(), []);

  const rilevatoriPosa = useMemo(() =>
    (utenti || [])
      .filter(u => /rilevatore|ril|squadra|posa/i.test((u.ruolo ?? u.role ?? "").toString()))
      .map(u => ({ id: idOf(u), nome: nameOf(u) }))
      .filter(x => x.id > 0 && x.nome)
      .sort((a,b)=> a.nome.localeCompare(b.nome, "it"))
  , [utenti, idOf, nameOf]);

  const responsabiliPosa = useMemo(() =>
    (utenti || [])
      .filter(u => /responsabile|resp|senior_posa|posa_senior|posa|senior/i.test((u.ruolo ?? u.role ?? "").toString()))
      .map(u => ({ id: idOf(u), nome: nameOf(u) }))
      .filter(x => x.id > 0 && x.nome)
      .sort((a,b)=> a.nome.localeCompare(b.nome, "it"))
  , [utenti, idOf, nameOf]);

// mappa locale id -> nome per la modale
const nameByIdLocal = useMemo(() => {
  const m = new Map();
  for (const u of (utenti || [])) {
    const id = Number(u.id ?? u.user_id ?? u.ID ?? 0);
    const nm = (u.nome ?? u.name ?? u.label ?? u.fullname ?? u.username ?? "").toString().trim();
    if (id > 0 && nm) m.set(id, nm);
  }
  return m;
}, [utenti]);

const nameFromIdLocal = useCallback((id) => {
  const n = Number(id || 0);
  return (Number.isFinite(n) && n > 0 && nameByIdLocal.get(n)) ? nameByIdLocal.get(n) : "";
}, [nameByIdLocal]);


  // ----- Applica riga BE al form (usato dopo salvataggi) -----
  const applyRowToEvent = React.useCallback((row) => {
    if (!row) return;

    // 1) allinea lo stato del form
    setForm((s) => ({
      ...s,
      // assegnazioni POSA
      rilevatore_id:     Number(row.rilevatore_id ?? s.rilevatore_id ?? 0),
      owner_user_id:     Number(row.owner_user_id ?? s.owner_user_id ?? 0),
      closed_by_user_id: Number(row.closed_by_user_id ?? s.closed_by_user_id ?? 0),

      // stato/esito
      stato_posa:  String(row.stato_posa ?? row.status ?? s.stato_posa ?? ""),
      esito_posa:  String(row.posa_esito ?? row.esito   ?? s.esito_posa ?? ""),

      // contratto / file / note  ← qui: più alias
      contratto_eur: Number(
        row.senior_importo_eur ??
        row.contratto_eur     ??
        row.contratto         ??
        row.senior_importo    ??
        row.importo_senior    ??
        s.senior_importo_eur  ??
        0
      ),

      budget_cliente_eur: Number(
        row.budget_cliente_eur ??
        s.budget_cliente_eur   ??
        0
      ),

      pdf_path:   String(row.pdf_path   ?? s.pdf_path   ?? ""),
      audio_url:  String(row.audio_path ?? s.audio_url  ?? ""), // mantieni il campo locale per il link "Apri"
      foto_path:  String(row.foto_path  ?? s.foto_path  ?? ""),
      note:       String(row.note ?? row.notes ?? s.note ?? s.notes ?? ""),
    }));

    // 2) sincronizza anche l'evento vivo su FullCalendar (extendedProps + stile)
    try {
      const live = (() => {
        try {
          const cal = evRef.current?.getCalendar?.();
          const liveIdRaw =
            evRef.current?.extendedProps?.agenda_id ??
            evRef.current?.id ??
            agendaId ?? null;
          const liveId = liveIdRaw != null ? String(liveIdRaw) : "";
          return (cal && liveId) ? cal.getEventById(liveId) : null;
        } catch { return null; }
      })();
      if (!live) return;

      const ex = live.extendedProps || {};
      const patched = {
        ...ex,
        // ID e assegnazioni
        rilevatore_id:     Number(row.rilevatore_id ?? ex.rilevatore_id ?? 0),
        owner_user_id:     Number(row.owner_user_id ?? ex.owner_user_id ?? 0),
        closed_by_user_id: Number(row.closed_by_user_id ?? ex.closed_by_user_id ?? 0),

        // stato/esito/UI
        stato_posa:   row.stato_posa ?? ex.stato_posa ?? row.status ?? ex.status ?? "",
        posa_esito:   row.posa_esito ?? row.esito ?? ex.posa_esito ?? ex.esito ?? "",
        ui_state_code: (row.ui_state_code ?? ex.ui_state_code ?? "S0").toString().toUpperCase(),

        // importi / budget
        senior_importo_eur: Number(
          row.senior_importo_eur ??
          row.contratto_eur     ??
          row.contratto         ??
          row.senior_importo    ??
          row.importo_senior    ??
          ex.senior_importo_eur ?? 0
        ),
        budget_cliente_eur: Number(row.budget_cliente_eur ?? ex.budget_cliente_eur ?? 0),

        // allegati + note
        pdf_path:   row.pdf_path   ?? ex.pdf_path   ?? "",
        audio_path: row.audio_path ?? ex.audio_path ?? "",
        foto_path:  row.foto_path  ?? ex.foto_path  ?? "",
        notes:      row.note ?? row.notes ?? ex.note ?? ex.notes ?? "",
        note:       row.note ?? row.notes ?? ex.note ?? ex.notes ?? "",
      };

      // aggiorna anche finestre orarie se le hai appena calcolate nel BE
      if (row.start) patched.start_dt = String(row.start).replace("T"," ");
      if (row.end)   patched.end_dt   = String(row.end).replace("T"," ");

      // commit su extendedProps
      Object.entries(patched).forEach(([k, v]) => live.setExtendedProp(k, v));
      live.setExtendedProp("_patched_at", Date.now());

      // restyle immediato coerente con il nuovo ui_state_code/esito
      applyUiStyleFromEx(live, patched);
    } catch {
      // ignora errori cosmetici
    }
  }, [agendaId, setForm]);

  // === SEQUENCER per evitare race tra click multipli ===
  const statusSeqRef = useRef(0);

  // ---- Helper: prendi SEMPRE l'istanza VIVA dell'evento dal calendario
  const getLiveEvent = useCallback(() => {
    try {
      const cal = evRef.current?.getCalendar?.();

      // ID vivo: priorità a extendedProps.agenda_id, poi event.id, poi agendaId
      const liveIdRaw =
        evRef.current?.extendedProps?.agenda_id ??
        evRef.current?.id ??
        agendaId ??
        null;

      const liveId = liveIdRaw != null ? String(liveIdRaw) : "";
      if (!cal || !liveId) return null;

      const live = cal.getEventById(liveId);
      return live || null;
    } catch {
      return null;
    }
  }, [agendaId]);


  /* ====================== PATCH helpers (DEVE stare prima di chi li usa) ====================== */
  const savePatchCore = useCallback(
    async (patch, { silentNoFields = false } = {}) => {
      setForbidden(false);

      // Usa SEMPRE l'ID vivo dell'evento
      const live = getLiveEvent();

      // Evito mixing ??/||: tutto con nullish e fallback finale
      const realId = Number(
        ((live?.extendedProps?.agenda_id ?? live?.id ?? agendaId) ?? 0)
      );

      console.debug("[POSA] savePatchCore -> agenda_id", realId, patch);

      const res = await fetch(`${API_POSA}/updateP_event.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        cache: "no-store",
        body: JSON.stringify({
          agenda_id: realId,
          ...patch,
          updated_by_operator_id:
            (Number.isFinite(Number(operatorId)) && Number(operatorId) > 0)
              ? Number(operatorId)
              : null,
        }),
      });

      const txt = await res.text();
      let data;
      try {
        data = JSON.parse(txt);
      } catch {
        data = { status: "ERR", message: "Invalid JSON", raw: txt };
      }

      const ok = res.ok && data?.status === "OK";
      const msg = (data?.hint || data?.message || "").toString();

      if (!ok) {
        if (res.status === 403) setForbidden(true);
        if (silentNoFields && /no\s+fields?\s+to\s+update/i.test(msg)) {
          return { ok: true, row: null, soft: true };
        }
        return { ok: false, message: msg || `HTTP ${res.status}` };
      }
      return { ok: true, row: data?.row || null };
    },
    [getLiveEvent, agendaId, operatorId]
  );

  const saveFirst = useCallback(
    async (patchList) => {
      for (const p of patchList) {
        const r = await savePatchCore(p, { silentNoFields: true });
        if (r.ok) return r;
      }
      return { ok: false, message: "Nessun alias riconosciuto dal server." };
    },
    [savePatchCore]
  );
  /* ====================== /PATCH helpers ====================== */

  // Applica colore ottimistico "safe" sull'istanza viva (se c'è)
  const applyImmediateColor = useCallback((uiCode) => {
    const ui = String(uiCode || "").toUpperCase();
    try {
      const live = getLiveEvent();
      if (!live) return;
      live.setExtendedProp("ui_state_code", ui);
      // Applica immediatamente lo stile coerente al nuovo codice
      applyUiStyleFromEx(live, { ...live.extendedProps, ui_state_code: ui });
    } catch {
      // non propagare: è solo un miglioramento visivo
    }
  }, [getLiveEvent]);

  // === STATO POSA — ottimistico + rollback sicuro ===
  const clickStato = useCallback(async (stato) => {
    if (busyRef.current || saving) return;
    busyRef.current = true;

    const WANT = String(stato || "").toLowerCase();
    const map = {
      in_corso:    { status: "in_corso",    stato_posa: null,                   ui: "S8" },
      pianificato: { status: "pianificato", stato_posa: "appuntamento_fissato", ui: "S8" },
      completato:  { status: "completato",  stato_posa: null,                   ui: "S6" },
      annullato:   { status: "annullato",   stato_posa: "annullato",            ui: "S7" },
    };
    const cfg = map[WANT] || map.in_corso;

    const prevStato = form.stato_posa || form.status || "";
    const prevCode  = (evRef.current?.extendedProps?.ui_state_code || "").toUpperCase() || "S0";
    const token = ++statusSeqRef.current;

    applyImmediateColor(cfg.ui);
    setForm((s) => ({ ...s, stato_posa: cfg.stato_posa || cfg.status }));

    setSaving(true);
    try {
      const payload = {
        status: cfg.status,
        ui_state_code: cfg.ui,
        ...(cfg.stato_posa ? { stato_posa: cfg.stato_posa } : {}),
      };
      const r = await savePatchCore(payload, { silentNoFields: false });

      if (token !== statusSeqRef.current) return;

      if (!r.ok) {
        applyImmediateColor(prevCode);
        setForm((s) => ({ ...s, stato_posa: prevStato }));
        alert(`Errore salvataggio stato: ${r.message || "generic"}`);
        return;
      }

      if (r.row) { try { applyRowToEvent(r.row); } catch {} }
      onDidChange?.();
    } finally {
      if (token === statusSeqRef.current) setSaving(false);
      busyRef.current = false;
    }
  }, [form.stato_posa, applyImmediateColor, savePatchCore, applyRowToEvent, onDidChange, saving]);

  // === ESITO POSA — ottimistico + rollback sicuro ===
  const clickEsito = useCallback(async (val) => {
    if (busyRef.current || saving) return;
    busyRef.current = true;

    const WANT = String(val || "").toLowerCase(); // "chiuso" | "non_chiuso"
    const nextCode = WANT === "chiuso" ? "S6" : WANT === "non_chiuso" ? "S5" : "S8";

    const prevEsito = String(form.esito_posa || "");
    const prevCode  = (evRef.current?.extendedProps?.ui_state_code || "").toUpperCase() || "S0";
    const token = ++statusSeqRef.current;

    applyImmediateColor(nextCode);
    setForm((s) => ({ ...s, esito_posa: WANT }));

    setSaving(true);
    try {
      const r = await savePatchCore({ esito_posa: WANT, ui_state_code: nextCode }, { silentNoFields: false });

      if (token !== statusSeqRef.current) return;

      if (!r.ok) {
        applyImmediateColor(prevCode);
        setForm((s) => ({ ...s, esito_posa: prevEsito }));
        alert(`Errore salvataggio esito: ${r.message || "generic"}`);
        return;
      }

      if (r.row) { try { applyRowToEvent(r.row); } catch {} }
      onDidChange?.();
    } finally {
      if (token === statusSeqRef.current) setSaving(false);
      busyRef.current = false;
    }
  }, [form.esito_posa, applyImmediateColor, savePatchCore, applyRowToEvent, onDidChange, saving]);





  // ----- Commit assegnazioni POSA -----
  const commitRilevatore = useCallback(async () => {
    if (busyRef.current || saving) return;
    busyRef.current = true;

    const next = Number(form.rilevatore_id || 0);
    const cur  = Number(ex.rilevatore_id || ex.ril_user_id || 0);
    if (next === cur) { busyRef.current = false; return; }

    setSaving(true);
    try {
      const r = await saveFirst([{ rilevatore_id: next || null }, { ril_user_id: next || null }]);
      if (!r.ok) { alert("Errore salvataggio Rilevatore"); return; }
      onDidChange?.();
    } finally {
      setSaving(false);
      busyRef.current = false;
    }
  }, [form.rilevatore_id, ex.rilevatore_id, ex.ril_user_id, saveFirst, onDidChange, saving]);

  const commitSenior = useCallback(async () => {
    if (busyRef.current || saving) return;
    busyRef.current = true;

    const next = Number(form.owner_user_id || 0);
    const cur  = Number(ex.owner_user_id || ex.senior_user_id || ex.senior_id || 0);
    if (next === cur) { busyRef.current = false; return; }

    setSaving(true);
    try {
      const r = await saveFirst([
        { owner_user_id:  next || null },
        { senior_user_id: next || null },
        { senior_id:      next || null },
      ]);
      if (!r.ok) { alert("Errore salvataggio Senior"); return; }
      onDidChange?.();
    } finally {
      setSaving(false);
      busyRef.current = false;
    }
  }, [form.owner_user_id, ex.owner_user_id, ex.senior_user_id, ex.senior_id, saveFirst, onDidChange, saving]);

  const commitChiusura = useCallback(async () => {
    if (busyRef.current || saving) return;
    busyRef.current = true;

    const next = Number(form.closed_by_user_id || 0);
    const cur  = Number(ex.closed_by_user_id || ex.chiusura_user_id || 0);
    if (next === cur) { busyRef.current = false; return; }

    setSaving(true);
    try {
      const r = await saveFirst([{ closed_by_user_id: next || null }, { chiusura_user_id: next || null }]);
      if (!r.ok) { alert("Errore salvataggio Chiusura"); return; }
      onDidChange?.();
    } finally {
      setSaving(false);
      busyRef.current = false;
    }
  }, [form.closed_by_user_id, ex.closed_by_user_id, ex.chiusura_user_id, saveFirst, onDidChange, saving]);

  // ----- Salva tutto (contratto/pdf/note) -----
  const doSaveAll = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      // 1) CONTRATTO POSA (prova più alias accettati dal BE)
      const val = Number.isFinite(Number(form.contratto_eur)) ? Number(form.contratto_eur) : null;
      if (val !== null) {
        const rC = await saveFirst([
          { senior_importo_eur: val },
          { contratto_eur:      val },
          { contratto:          val },
          { senior_importo:     val },
          { importo_senior:     val },
        ]);
        if (!rC.ok) { alert("Errore salvataggio contratto: " + (rC.message || "unknown")); setSaving(false); return; }
        if (rC.row) { try { applyRowToEvent(rC.row); } catch {} }
      }

      // 2) BUDGET CLIENTE (nuovo campo)
      const bud = Number.isFinite(Number(form.budget_cliente_eur)) ? Number(form.budget_cliente_eur) : null;
      if (bud !== null) {
        const rB = await saveFirst([
          { budget_cliente_eur: bud }, // colonna DB consigliata
          { budget_cliente:     bud }, // alias possibili lato PHP
          { budget_eur:         bud },
        ]);
        if (!rB.ok) { alert("Errore salvataggio budget: " + (rB.message || "unknown")); setSaving(false); return; }
        if (rB.row) { try { applyRowToEvent(rB.row); } catch {} }
      }

      // 3) NOTE + PDF
      const rNP = await savePatchCore({
        notes:   String(form.note || ""),
        note:    String(form.note || ""),
        pdf_path:String(form.pdf_path || ""),
      }, { silentNoFields:true });
      if (!rNP.ok) { alert(rNP.message || "Errore salvataggio note/PDF"); setSaving(false); return; }

      setSaved(true);
      setTimeout(() => setSaved(false), 900);
      onDidChange?.();
    } finally {
      setSaving(false);
    }
  }, [saving, form.contratto_eur, form.budget_cliente_eur, form.note, form.pdf_path, saveFirst, savePatchCore, applyRowToEvent, onDidChange]);


const onPdfSelect = useCallback(async (e) => {
  const f = e.target.files?.[0]; if (!f) return;
  if (f.size > 15 * 1024 * 1024) { alert("PDF troppo grande (max 15 MB)."); e.target.value=""; return; }

  try {
    setSaving(true);
    const url = await doUpload("pdf", f);          // 1) upload fisico → URL
    setField("pdf_path", url);                     // 2) aggiorna UI locale

    // 3) salva su DB (errore esplicito se rifiutato)
    const r = await savePatchCore({ pdf_path: url }, { silentNoFields: false });
    if (!r.ok) throw new Error(r.message || "Salvataggio PDF fallito");

    // 4) applica eventuale row di ritorno (sincronizza extendedProps + stile)
    if (r.row) try { applyRowToEvent(r.row); } catch {}

    // 5) refetch/notify
    onDidChange?.();
  } catch (err) {
    if (String(err?.message || "").toLowerCase().includes("cancellato")) return;
    alert(err?.message || "Errore upload (rete/Firefox)");
  } finally {
    setSaving(false);
    e.target.value = "";
  }
}, [doUpload, savePatchCore, onDidChange, setField, applyRowToEvent]);

const onAudioSelect = useCallback(async (e) => {
  const f = e.target.files?.[0]; if (!f) return;
  if (f.size > 50 * 1024 * 1024) { alert("Audio troppo grande (max 50 MB)."); e.target.value=""; return; }

  try {
    setSaving(true);
    const url = await doUpload("audio", f);        // 1) upload fisico → URL
    setField("audio_url", url);                    // 2) mostra subito il link
    const next = (form.note ? form.note + "\n" : "") + `[AUDIO] ${url}`;
    setField("note", next);

    // 3) salva anche la colonna DB corretta + note (errore esplicito)
    const r = await savePatchCore({ audio_path: url, note: next, notes: next }, { silentNoFields: false });
    if (!r.ok) throw new Error(r.message || "Salvataggio Audio fallito");

    // 4) applica riga BE se presente
    if (r.row) try { applyRowToEvent(r.row); } catch {}

    // 5) refetch/notify
    onDidChange?.();
  } catch (err) {
    if (String(err?.message || "").toLowerCase().includes("cancellato")) return;
    alert(err?.message || "Errore upload Audio");
  } finally {
    setSaving(false);
    e.target.value = "";
  }
}, [form.note, doUpload, savePatchCore, onDidChange, setField, applyRowToEvent]);



  // In chiusura modale, invalida eventuali risposte e sblocca i click
  useEffect(() => {
    return () => {
      statusSeqRef.current++;
      busyRef.current = false;
    };
  }, []);

  // ----- Header: mappa + intestazione (no Hook qui) -----
  const addr = ex.cliente_indirizzo || ex.indirizzo || "";
  const mapHref =
    (ex.addr_lat && ex.addr_lng)
      ? `https://maps.google.com/?q=${encodeURIComponent(`${ex.addr_lat},${ex.addr_lng}`)}`
      : ([addr, ex.comune, ex.cap].some(Boolean)
          ? `https://maps.google.com/?q=${encodeURIComponent([addr, ex.comune, ex.cap].filter(Boolean).join(", "))}`
          : null);

  const startIsoForHeader = (ex.start_dt && typeof ex.start_dt === "string") ? ex.start_dt.replace(" ", "T") : "";
  const dForHeader = startIsoForHeader ? new Date(startIsoForHeader) : null;
  const HH = (x) => String((x?.getHours?.() ?? 0)).padStart(2, "0");
  const MM = (x) => String((x?.getMinutes?.() ?? 0)).padStart(2, "0");
  const ora = dForHeader ? `${HH(dForHeader)}:${MM(dForHeader)}` : "--:--";
  const cliente = String(ex.cliente_nome || ex.nome_cliente || "Cliente");

  // === RENDER: condizione SOLO qui, non prima degli Hook ===
  return (!open || !event) ? null : (
    <div className="Modal" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999 }}>
      <div style={{ width:780, maxWidth:"95vw", maxHeight:"92vh", overflow:"auto", background:"#fff", borderRadius:12, boxShadow:"0 10px 40px rgba(0,0,0,.25)" }}>
        {/* header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderBottom:"1px solid #eee" }}>
          <div style={{ fontWeight:800 }}>
            POSA · Evento #{agendaId} &nbsp;
            {mapHref ? <a href={mapHref} target="_blank" rel="noopener noreferrer">🔗 Mappa</a> : null}
          </div>
          <button onClick={onClose} title="Chiudi" className="btn-icon">✕</button>
        </div>

        <div style={{ padding:12 }}>
          {/* intestazione nominativa */}
          <div style={{ fontSize:14, fontWeight:800, marginBottom:8 }}>
            {ora} &nbsp; {cliente} {ex.comune ? `· ${ex.comune}` : ""} {ex.cap ? `· ${ex.cap}` : ""}
          </div>

          {/* STATO / ESITO */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, margin:"6px 0 12px" }}>
            <button type="button" className={`chip ${form.stato_posa==="in_corso" ? "chip-orange on" : "chip-orange"}`} onClick={() => clickStato("in_corso")} disabled={saving}>🚧 In corso</button>
            <button type="button" className={`chip ${form.esito_posa==="non_chiuso" ? "chip-red on" : "chip-red"}`}   onClick={() => clickEsito("non_chiuso")} disabled={saving}>🟥 Non chiuso</button>
            <button type="button" className={`chip ${form.esito_posa==="chiuso" ? "chip-deepgreen on" : "chip-deepgreen"}`} onClick={() => clickEsito("chiuso")} disabled={saving}>🟩 Chiuso</button>
            <button type="button" className={`chip ${form.stato_posa==="annullato" ? "chip-brown on" : "chip-brown"}`} onClick={() => clickStato("annullato")} disabled={saving}>⛔ Annullato</button>
          </div>

          {/* Assegnazioni POSA */}
          <div className="am-rows-3" style={{ marginBottom:10 }}>
            <div className="am-row three">
              {/* Rilevatore POSA */}
              <div className="am-cell am-col">
                <label className="am-lbl">Junior POSA</label>
                <div className="am-sel-row">
                  <select
                    className="am-sel"
                    value={form.rilevatore_id}
                    onChange={(e)=> setField("rilevatore_id", Number(e.target.value))}
                    disabled={saving}
                    title="Assegna squadra/rilevatore POSA"
                  >
                    <option value={0}>— Nessuno —</option>
                    {rilevatoriPosa.map(r => (
                      <option key={r.id} value={r.id}>{r.nome}</option>
                    ))}
                  </select>
                  <button className="am-btn am-ghost" disabled={saving} onClick={commitRilevatore} title="Assegna">✔︎</button>
                </div>
              </div>

              {/* Senior POSA */}
              <div className="am-cell am-col">
                <label className="am-lbl">Senior (POSA)</label>
                <div className="am-sel-row">
                  <select
                    className="am-sel"
                    value={form.owner_user_id}
                    onChange={(e)=> setField("owner_user_id", Number(e.target.value))}
                    disabled={saving}
                    title="Assegna Senior POSA"
                  >
                    <option value={0}>— Nessuno —</option>
                    {responsabiliPosa.map(r => (
                      <option key={r.id} value={r.id}>{r.nome}</option>
                    ))}
                  </select>
                  <button className="am-btn am-ghost" disabled={saving} onClick={commitSenior} title="Assegna">✔︎</button>
                </div>
              </div>

              {/* Chiusura */}
              <div className="am-cell am-col">
                <label className="am-lbl">Chiusura</label>
                <div className="am-sel-row">
                  <select
                    className="am-sel"
                    value={form.closed_by_user_id}
                    onChange={(e)=> setField("closed_by_user_id", Number(e.target.value))}
                    disabled={saving}
                    title="Assegna chiusura"
                  >
                    <option value={0}>— Nessuno —</option>
                    {responsabiliPosa.map(r => (
                      <option key={r.id} value={r.id}>{r.nome}</option>
                    ))}
                  </select>
                  <button className="am-btn am-ghost" disabled={saving} onClick={commitChiusura} title="Assegna">✔︎</button>
                </div>
              </div>
            </div>
          </div>

          {/* Riga 1: AP + contratto POSA */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, marginBottom:10 }}>
            <div>
  <div className="am-lbl">Rilevatore (AP)</div>
  <div style={{ fontWeight:700 }}>
    { nameFromIdLocal(ex.ap_rilevatore_id) || ex.ap_rilevatore_name || "—" }
  </div>
</div>
<div>
  <div className="am-lbl">Senior (AP)</div>
  <div style={{ fontWeight:700 }}>
    { nameFromIdLocal(ex.ap_senior_id) || ex.ap_senior_name || "—" }
  </div>




              <div className="am-lbl">Valore contratto POSA (€)</div>
              <input
                type="number" min={0} step={50}
                value={Number.isFinite(Number(form.contratto_eur)) ? form.contratto_eur : 0}
                onChange={(e)=> setField("contratto_eur", Math.max(0, Number(e.target.value || 0)))}
                disabled={saving}
                style={{
                  width:"100%", padding:"10px 12px", borderRadius:12,
                  border:"1px solid #e5e7eb", outline:"none",
                  boxShadow: form.esito_posa === "chiuso" ? "0 0 0 3px rgba(16,185,129,.35)" : "none"
                }}
                placeholder="0"
              />
            </div>

            <div>
              <div className="am-lbl">Budget cliente (€)</div>
              <input
                type="number" min={0} step={50}
                value={Number.isFinite(Number(form.budget_cliente_eur)) ? form.budget_cliente_eur : 0}
                onChange={(e)=> setField("budget_cliente_eur", Math.max(0, Number(e.target.value || 0)))}
                disabled={saving}
                style={{ width:"100%", padding:"10px 12px", borderRadius:12, border:"1px solid #e5e7eb", outline:"none" }}
                placeholder="0"
              />
            </div>
          </div>

          {/* Riga 2: PDF · Note · Audio */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1.2fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <div className="am-lbl">PDF (upload)</div>
              <input
                className="am-input am-url am-url-sm"
                value={form.pdf_path}
                onChange={(e)=> setField("pdf_path", e.target.value)}
                disabled={saving}
                placeholder="/uploads/posa/contratto.pdf"
              />
              <div style={{marginTop:6}}>
                <button type="button" className="chip chip-sm" onClick={()=> document.getElementById(`pdfPosaHidden-${agendaId}`)?.click()} disabled={saving}>⬆️ Carica PDF</button>
                {form.pdf_path ? (
                  <button type="button" className="chip chip-sm" style={{ marginLeft:6 }} onClick={()=> window.open(form.pdf_path, "_blank","noopener,noreferrer")}>🔗 Apri</button>
                ) : null}
              </div>
              <input id={`pdfPosaHidden-${agendaId}`} type="file" accept="application/pdf,.pdf" style={{ display:"none" }} onChange={onPdfSelect} />
            </div>

            <div>
              <div className="am-lbl">Note generiche</div>
              <textarea
                rows={3}
                value={form.note}
                onChange={(e)=> setField("note", e.target.value)}
                disabled={saving}
                style={{width:"100%", resize:"vertical"}}
                placeholder="Annotazioni libere…"
              />
            </div>

            <div>
              <div className="am-lbl">Audio (upload)</div>
              <input
                className="am-input am-url am-url-sm"
                value={form.audio_url}
                onChange={(e)=> setField("audio_url", e.target.value)}
                disabled
                placeholder="(link mostrato dopo upload)"
              />
              <div style={{marginTop:6}}>
                <button type="button" className="chip chip-sm" onClick={()=> document.getElementById(`audioPosaHidden-${agendaId}`)?.click()} disabled={saving}>⬆️ Carica Audio</button>
                {form.audio_url ? (
                  <button type="button" className="chip chip-sm" style={{ marginLeft:6 }} onClick={()=> window.open(form.audio_url, "_blank","noopener,noreferrer")}>🔗 Apri</button>
                ) : null}
              </div>
              <input id={`audioPosaHidden-${agendaId}`} type="file" accept="audio/*" style={{ display:"none" }} onChange={onAudioSelect} />
            </div>
          </div>

          {/* AZIONI */}
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button type="button" className="chip chip-primary chip-sm" onClick={doSaveAll} disabled={saving}>💾 Salva tutto</button>
            <button type="button" className="chip chip-sm" onClick={onClose} disabled={saving}>✕ Chiudi</button>
          </div>

          {saved && (
            <div role="status" aria-live="polite"
                  style={{ position:"fixed", right:12, bottom:12, background:"#16a34a", color:"#fff", padding:"8px 10px", borderRadius:10, boxShadow:"0 8px 20px rgba(22,163,74,.25)" }}>
              Salvato ✓
            </div>
          )}
          {forbidden && (
            <div style={{ color:"#b91c1c", marginTop:8 }}>
              Permesso negato: contatta l’amministratore per abilitarlo.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// <<< CHIUSURA SICURA di ActionModalPosa (mancante)

/* ============================================================
   COMPONENT PRINCIPALE
   ============================================================ */





export default function AgendaPosa() {
  const calRef = useRef(null);

  // Auth
  const auth = useAuth?.() || {};
  const user = auth?.user || auth || {};
  const role = roleKeyFromNorm(user?.role || user?.ruolo || "admin");
  const caps = useMemo(() => deriveRoleCapsPosa(role), [role]);

  const UID = useMemo(() => {
    const n = Number(user?.id ?? user?.ID ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [user]);

  const [openPosa, setOpenPosa] = useState(false);
  const [posEvent, setPosEvent] = useState(null);
  const [zonaId, setZonaId] = useState(() => Number(user?.zona_id || 1));
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // utenti per badge
  const [utenti, setUtenti] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_USERS}/users_list.php?zona_id=${zonaId}`, { credentials:"same-origin" });
        const j = await r.json().catch(()=> ({}));
        const arr =
          Array.isArray(j?.rows)   ? j.rows :
          Array.isArray(j?.users)  ? j.users :
          Array.isArray(j)         ? j      : [];
        if (alive) setUtenti(arr);
      } catch { if (alive) setUtenti([]); }
    })();
    return () => { alive = false; };
  }, [zonaId]);

  const nameById = useMemo(() => {
    const m = new Map();
    for (const u of (utenti || [])) {
      const id = Number(u.id ?? u.user_id ?? u.ID ?? 0);
      const nm = (u.nome ?? u.name ?? u.label ?? u.fullname ?? u.username ?? "").toString().trim();
      if (id > 0 && nm) m.set(id, nm);
    }
    return m;
  }, [utenti]);

  const normRole = useCallback((u)=> String(u.ruolo ?? u.role ?? "").toLowerCase(), []);
  const labelOf  = useCallback((u)=> (u.nome ?? u.name ?? u.label ?? u.fullname ?? u.username ?? "").toString().trim(), []);
  const idOf     = useCallback((u)=> Number(u.id ?? u.user_id ?? u.ID ?? 0), []);

  // Nome da id con fallback
  const nameFromId = useCallback((id) => {
    const n = Number(id || 0);
    return (Number.isFinite(n) && n > 0 && nameById.get(n)) ? nameById.get(n) : null;
  }, [nameById]);




const eventDidMount = useCallback((info) => {
  try {
    info.el.removeAttribute("title");
    const start = info.event.start;
    const allowPhone = (!caps.phoneDelay || !start)
      ? true
      : (Date.now() >= (start.getTime() - 20 * 60 * 1000)); // 20'

    // ✅ Applica lo stile in base agli extendedProps (ui_state_code ecc.)
    applyUiStyleFromEx(info.event, info.event.extendedProps || {});

    info.el._apTipCleanup = installAgendaProTooltip(info.el, info.event, { allowPhone });
  } catch {}
}, [caps.phoneDelay]);











// const eventWillUnmount = useCallback((info) => {
//   try { info.el._apCleanup?.(); } catch {}
// }, []);




  // Responsabili (per i due menu in modale)
  const responsabili = useMemo(() => (
    (utenti || [])
      .filter(u => /responsabile|resp|senior_posa|posa_senior|posa/.test(normRole(u)))
      .map(u => ({ id: idOf(u), nome: labelOf(u) }))
      .filter(x => x.id > 0 && x.nome)
      .sort((a,b)=> a.nome.localeCompare(b.nome, "it"))
  ), [utenti, normRole, idOf, labelOf]);

  const refetch = useCallback(() => {
    try { calRef.current?.getApi?.().refetchEvents(); } catch {}
  }, []);

useEffect(() => {
  let ch = null;
  try {
    ch = new BroadcastChannel("agenda_sync");
    ch.onmessage = (ev) => {
      const t = ev?.data?.type || "";

      // 🔄 In AgendaPosa reagiamo solo ai "changed" generali (es. modifiche da AgendaPro)
      // e IGNORIAMO i "posa:changed" che mandiamo noi stessi quando trasciniamo
      if (t === "changed") {
        setTimeout(refetch, 120);
      }
    };
  } catch {}
  return () => {
    try { ch?.close(); } catch {}
  };
}, [refetch]);






// 🔄 Auto-refresh disabilitato: usiamo solo BroadcastChannel + bottone "Ricarica"
// useEffect(() => {
//   let tid = null;
//   const tick = () => {
//     if (document.visibilityState === "visible") refetch();
//     tid = window.setTimeout(tick, 10000);
//   };
//   tid = window.setTimeout(tick, 10000);
//   return () => { if (tid) clearTimeout(tid); };
// }, [refetch]);





  


  const eventsSource = useCallback(async (info, success, failure) => {
  setLoading(true);
  try {
    // usa gli oggetti Date (mai startStr/endStr) → stringhe SQL locali
    const toSqlParam = (d) => dateToSql(d);
    const startParam = toSqlParam(info.start);
    const endParam   = toSqlParam(info.end);

    // builder URL
    const buildUrl = (zona) =>
      `${API_POSA}/listP_events.php` +
      `?start=${encodeURIComponent(startParam)}` +
      `&end=${encodeURIComponent(endParam)}` +
      `&zona_id=${encodeURIComponent(zona)}` +
      (caps.restrictAssignedOnly && UID ? `&assigned_to=${encodeURIComponent(UID)}` : "") +
      (q ? `&q=${encodeURIComponent(q)}` : "") +
      `&v=${Date.now()}`; // cache-buster

    // ---- tentativo 1: con zona corrente ----
    const url1 = buildUrl(zonaId);
    const resp1 = await apiGet(url1);
    let raw =
      Array.isArray(resp1)         ? resp1 :
      Array.isArray(resp1?.rows)   ? resp1.rows :
      Array.isArray(resp1?.data)   ? resp1.data :
      Array.isArray(resp1?.events) ? resp1.events : [];

    console.debug("[POSA] fetch #1", { url: url1, count: raw.length, sample: raw[0] });

    // ---- se vuoto e zona > 0, retry senza zona (fallback) ----
    if ((!raw || raw.length === 0) && Number(zonaId) > 0) {
      const url2 = buildUrl(0);
      const resp2 = await apiGet(url2);
      raw =
        Array.isArray(resp2)         ? resp2 :
        Array.isArray(resp2?.rows)   ? resp2.rows :
        Array.isArray(resp2?.data)   ? resp2.data :
        Array.isArray(resp2?.events) ? resp2.events : [];
      console.debug("[POSA] fetch #2 (fallback no zona)", { url: url2, count: raw.length, sample: raw[0] });
    }

    // filtro “solo assegnati” se attivo
    const filtered = (caps.restrictAssignedOnly && UID)
      ? raw.filter(row => {
          const respId = Number(row.owner_user_id ?? row.responsabile_id ?? row.senior_user_id ?? row.senior_id ?? 0);
          const rilId  = Number(row.rilevatore_id ?? row.ril_user_id ?? 0);
          const closed = Number(row.closed_by_user_id ?? row.chiusura_user_id ?? 0);
          return [respId, rilId, closed].includes(UID);
        })
      : raw;

    // mappa → FullCalendar EventInput
    const evs = filtered.map((row) => {
      if (!row) return null;

      // assicurati che ci siano id/start/end per l’adapter
      const r = { ...row };
      if (r.id == null) r.id = r.agenda_id ?? `ev_${Math.random().toString(36).slice(2)}`;
      if (!r.start) r.start = r.start_dt ?? r.planned_start ?? null;
      if (!r.end)   r.end   = r.end_dt   ?? r.planned_end   ?? null;

      const e = adaptPosaEvent(r);
      if (!e) return null;

      // arricchisci extendedProps con allegati e nomi
      const respId = Number(r.owner_user_id ?? r.responsabile_id ?? r.senior_user_id ?? r.senior_id ?? 0);
      const rilId  = Number(r.rilevatore_id ?? r.ril_user_id ?? 0);
      const closedId = Number(r.closed_by_user_id ?? r.chiusura_user_id ?? r.closed_id ?? 0);

      const rilPosNm = nameById.get(rilId)  || r.rilevatore_name || "";
      const senPosNm = nameById.get(respId) || r.owner_name || r.responsabile_name || r.senior_name || "";
      let   chiNm    = nameById.get(closedId) || r.chiusura_name || r.closer_name || r.closed_by_name || "";

      if (!chiNm && closedId > 0 && Array.isArray(utenti) && utenti.length) {
        const u = utenti.find(u => Number(u.id ?? u.user_id ?? u.ID ?? 0) === closedId);
        if (u) chiNm = (u.nome ?? u.name ?? u.label ?? u.fullname ?? u.username ?? "").toString().trim();
      }

      const ex = e.extendedProps || {};
      const ext = {
        ...ex,
        telefono: r.telefono || r.phone || r.cliente_telefono || r.cliente_tel || "",
         ap_rilevatore_id: r.ap_rilevatore_id ?? null,
 ap_senior_id:     r.ap_senior_id     ?? null,
 ap_rilevatore_name: (r.ap_rilevatore_id ? nameById.get(Number(r.ap_rilevatore_id)) : "") || ex.ap_rilevatore_name || "",
 ap_senior_name:     (r.ap_senior_id     ? nameById.get(Number(r.ap_senior_id))     : "") || ex.ap_senior_name     || "",
        rilevatore:         { id: rilId,  nome: rilPosNm },
        senior:             { id: respId, nome: senPosNm },
        closed_by_user_id:  closedId,
        chiusura_user_id:   closedId,
        rilevatore_name:    rilPosNm,
        responsabile_name:  senPosNm,
        chiusura_name:      chiNm,
        pdf_path:   r.pdf_path   ?? ex.pdf_path   ?? "",
        audio_path: r.audio_path ?? ex.audio_path ?? "",
        foto_path:  r.foto_path  ?? ex.foto_path  ?? "",
        status:     r.status     ?? ex.status,
        posa_esito: r.posa_esito ?? r.esito_posa ?? r.esito ?? ex.posa_esito,
      };

      e.title = e.title || ext.cliente_nome || r.cliente_nome || "Appuntamento";
      return { ...e, extendedProps: ext };
    }).filter(ev => !!ev && !!ev.id && !!ev.start);

    console.debug("[POSA] mapped events", { count: evs.length });
    success?.(evs);
  } catch (err) {
    console.error("[AgendaPosa events]", err);
    failure?.(err);
  } finally {
    setLoading(false);
  }
}, [zonaId, q, caps.restrictAssignedOnly, UID, nameById, utenti]);








  const onEventDragStart = useCallback((arg) => {
    const ev = arg.event;
    ev.setExtendedProp("_dragFrom", {
      start: dateToSql(ev.start),
      end:   dateToSql(ev.end || ev.start)
    });
  }, []);

  const persistMoveResize = useCallback(
  async (chg) => {
    if (!caps.canMoveEvent || caps.readOnly) {
      chg.revert?.();
      return;
    }

    const ev = chg.event;
    const agendaId = Number(ev.extendedProps?.agenda_id ?? ev.id);

    if (!Number.isFinite(agendaId) || agendaId <= 0) {
      chg.revert?.();
      return;
    }

    try {
      const startSql = dateToSql(ev.start);
      const endSql   = dateToSql(ev.end || ev.start);
      const durMin   = Math.max(
        15,
        Math.round(((ev.end || ev.start) - ev.start) / 60000)
      );

      console.debug("[POSA drag]", {
        agendaId,
        from: ev.extendedProps?.start_dt,
        to:   startSql,
        durMin,
      });

      const res = await apiPost(
        `${API_POSA}/updateP_event.php`,
        {
          agenda_id:     agendaId,

          // campi orari “classici”
          start_dt:      startSql,
          end_dt:        endSql,

          // pianificazione POSA
          planned_start: startSql,
          duration_min:  durMin,
          fascia:        fasciaFromDate(ev.start),
        },
        { headers: { "X-Debug": "1" } }
      );

      console.debug("[POSA drag] OK", res);

      // aggiorna subito i dati locali
      ev.setExtendedProp("planned_start", startSql);
      ev.setExtendedProp("start_dt",      startSql);
      ev.setExtendedProp("end_dt",        endSql);
      ev.setExtendedProp("fascia",        fasciaFromDate(ev.start));

      try {
        const ch = new BroadcastChannel("agenda_sync");
        ch.postMessage({ type: "posa:changed", at: Date.now() });
        ch.close();
      } catch {}
    } catch (e) {
      console.error("[POSA drag] ERROR", e);
      alert(`Errore salvataggio: ${e.message || e}`);
      chg.revert?.();
    }
  },
  [caps]
);









  const onEventClickPosa = useCallback((arg) => {
    arg.jsEvent?.preventDefault();
    arg.jsEvent?.stopPropagation();
    setPosEvent(arg.event);
    setOpenPosa(true);
  }, []);

  const headerToolbar = useMemo(
    () => ({
      left: "prev,next today",
      center: "title",
      right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
    }),
    []
  );

  return (
    <div className="AgendaPro page-container" style={{ padding: 12 }}>
      <h2 className="page-title">
        Agenda <span className="sub">POSA</span>
        {loading ? <span className="loading"> · Carico…</span> : null}
      </h2>

      <PermessiBarPosa role={role} zonaId={zonaId} caps={caps} />

      <div className="ops-bar">
        <div className="ops-left">
          <label className="ops-lbl">Cerca</label>
          <input
            placeholder="nome / telefono / indirizzo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="ops-right" style={{ gap: 8 }}>
          <label className="ops-lbl" style={{ marginRight: 6 }}>Zona</label>
          <select
            value={zonaId}
            onChange={(e) => setZonaId(Number(e.target.value))}
          >
            <option value={1}>Verona</option>
            <option value={2}>Modena</option>
            <option value={3}>Novara</option>
            <option value={4}>Milano Sud</option>
            <option value={5}>Milano Nord</option>
          </select>
          <button onClick={refetch} disabled={loading} title="Ricarica">
            Ricarica
          </button>
        </div>
      </div>

      <FullCalendar
        ref={calRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
        initialView="timeGridWeek"
        headerToolbar={headerToolbar}
        buttonText={{
          today: "Oggi",
          month: "Mese",
          week:  "Settimana",
          day:   "Giorno",
          list:  "Lista"
        }}
        locales={[itLocale]}
        locale="it"
        height="auto"
        firstDay={1}
        hiddenDays={[0]}
        allDaySlot={false}
        nowIndicator
        expandRows
        slotDuration="00:30"
        slotLabelInterval="01:00"
        slotMinTime="07:00:00"
        slotMaxTime="20:00:00"
        editable={!!caps.canMoveEvent && !caps.readOnly}
        eventDurationEditable={!!caps.canMoveEvent && !caps.readOnly}
        eventStartEditable={!!caps.canMoveEvent && !caps.readOnly}
        eventOverlap
        events={eventsSource}
        eventDragStart={onEventDragStart}
        eventDrop={persistMoveResize}
        eventResize={persistMoveResize}
        eventClick={onEventClickPosa}
        eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
        eventDisplay="block"
        eventContent={EventContentPosa}
        eventDidMount={eventDidMount}
        eventWillUnmount={(info) => {
            try { if (info.el._apTipCleanup) info.el._apTipCleanup(); } catch {}
        }}
      />

      {posEvent && (
        <ActionModalPosa
          open={openPosa}
          onClose={() => setOpenPosa(false)}
          event={posEvent}
          operatorId={UID || 0}
          responsabili={responsabili}
          caps={caps}
onDidChange={() => {
  refetch();
  try {
    const ch = new BroadcastChannel("agenda_sync");
    ch.postMessage({ type: "posa:changed", at: Date.now() });
    if (ch) ch.close();
  } catch {}
}}
        />
      )}

      {loading && <div className="posa-loading">Caricamento…</div>}
    </div>
  );
}
